import React from "react";
import {
  Building2,
  Eye,
  Heart,
  MessageSquare,
  TrendingUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import QuickActionsMenu from "../QuickActionsMenu";
import {
  getActivationCtaLabel,
  getAssignedPlan,
  getDerivedPaymentStatus,
  getPlanLabel,
  getRoleLabel,
  getStatusBadgeClass,
  getStatusLabel,
  requiresActivation,
} from "../../constants/plans";
import { NAV_LABELS } from "../../constants/uiCopy";

const ADMIN_PLAN_LABEL = "Acceso Administrativo";

const DashboardHeader = ({
  user,
  currentUser,
  stats,
  developerProfile,
  handleUpgrade,
  isActivatingSubscription,
  navigate,
}) => {
  const role = currentUser?.role || user?.role;
  const plan = getAssignedPlan(currentUser || user);
  const paymentStatus = getDerivedPaymentStatus(currentUser || user);
  const isPendingActivation = requiresActivation(currentUser || user);
  const planLabel = role === "admin" ? ADMIN_PLAN_LABEL : getPlanLabel(plan);
  const activationCtaLabel = getActivationCtaLabel(currentUser || user);

  const roleActions = {
    basic: [
      {
        label: "Explorar propiedades",
        action: () => navigate("/search"),
        variant: "default",
      },
      {
        label: "Ver favoritos",
        action: () => navigate("/favorites"),
        variant: "outline",
      },
      {
        label: "Mi Cuenta",
        action: () => navigate("/account"),
        variant: "outline",
      },
    ],
    owner: [
      {
        label: isPendingActivation ? activationCtaLabel : "Nueva Propiedad",
        action: () =>
          isPendingActivation ? handleUpgrade() : navigate("/add-property"),
        variant: "default",
        disabled: isPendingActivation && isActivatingSubscription,
      },
      {
        label: "Mi Cuenta",
        action: () => navigate("/account?tab=subscription"),
        variant: "outline",
      },
    ],
    broker: [
      {
        label: isPendingActivation ? activationCtaLabel : "Nueva Propiedad",
        action: () =>
          isPendingActivation
            ? handleUpgrade()
            : navigate("/add-property"),
        variant: "default",
        disabled: isPendingActivation && isActivatingSubscription,
      },
      {
        label: `Ver ${NAV_LABELS.collaborationInventory}`,
        action: () => navigate("/inventario-brokers"),
        variant: "outline",
      },
      {
        label: "Mi Perfil",
        action: () => navigate("/broker-profile/edit"),
        variant: "outline",
      },
      {
        label: "Mi Cuenta",
        action: () => navigate("/account?tab=subscription"),
        variant: "outline",
      },
    ],
    developer: [
      {
        label: isPendingActivation ? activationCtaLabel : "Nuevo Desarrollo",
        action: () =>
          isPendingActivation ? handleUpgrade() : navigate("/add-development"),
        variant: "default",
        disabled: isPendingActivation && isActivatingSubscription,
      },
      ...(!isPendingActivation
        ? [
            {
              label: "Nueva Propiedad",
              action: () => navigate("/add-property"),
              variant: "outline",
            },
            {
              label: `Ver ${NAV_LABELS.collaborationInventory}`,
              action: () => navigate("/inventario-brokers"),
              variant: "outline",
            },
          ]
        : []),
      {
        label: "Mi Perfil",
        action: () => navigate("/developer-profile/edit"),
        variant: "outline",
      },
      {
        label: "Mi Cuenta",
        action: () => navigate("/account?tab=subscription"),
        variant: "outline",
      },
    ],
    admin: [
      {
        label: "Gestionar usuarios",
        action: () => navigate("/admin/users"),
        variant: "default",
      },
      {
        label: "Configuración",
        action: () => navigate("/admin/settings"),
        variant: "outline",
      },
    ],
  };

  const actions = roleActions[role] || [];
  const primaryAction = actions[0] || null;
  const secondaryActions = actions.slice(1);

  const tasksByRole = {
    basic: [
      "Explora nuevas propiedades en tu zona",
      "Guarda oportunidades en favoritos",
      "Revisa tu límite de publicación",
    ],
    owner: [
      "Publica o actualiza tu inventario",
      "Responde consultas prioritarias",
      "Revisa estado de suscripción",
    ],
    broker: [
      `Consulta ${NAV_LABELS.collaborationInventory}`,
      "Actualiza leads en tu embudo",
      "Registra avances comerciales",
    ],
    developer: [
      "Actualiza estatus de desarrollos",
      "Verifica unidades disponibles",
      "Activa canales para brokers",
    ],
  };

  const todayTasks = tasksByRole[role] || [];

  return (
    <>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 md:mb-8 gap-4">
        <div>
          <h1
            className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2 tracking-tight"
            style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
            data-testid="dashboard-title"
          >
            Dashboard
          </h1>
          <p className="text-sm md:text-base text-muted-foreground">
            Bienvenido, {currentUser?.name || user?.name} · {getRoleLabel(role)}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-xs px-3 py-1 rounded-full font-semibold bg-primary/10 text-primary">
              {planLabel}
            </span>
            {role !== "admin" && (
              <span
                className={`text-xs px-3 py-1 rounded-full font-semibold ${getStatusBadgeClass(paymentStatus)}`}
              >
                {getStatusLabel(paymentStatus)}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-col items-stretch gap-2 w-full sm:w-auto sm:items-end">
          <QuickActionsMenu user={currentUser || user} />
          {actions.map((action) => (
            <Button
              key={action.label}
              variant={action.variant}
              onClick={action.action}
              disabled={action.disabled}
              className="flex-1 sm:flex-none"
            >
              {action.disabled ? "Redirigiendo..." : action.label}
            </Button>
          ))}
        </div>
      </div>

      {todayTasks.length > 0 && (
        <div className="bg-background border border-border rounded-lg p-4 mb-6">
          <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground mb-2">
            Tareas de hoy
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {todayTasks.map((task) => (
              <div
                key={task}
                className="text-sm rounded-md border border-border bg-muted/20 px-3 py-2"
              >
                {task}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stats badges */}
      {role === "admin" && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="bg-background border border-border rounded-lg p-4 text-center">
            <Building2 className="w-5 h-5 mx-auto mb-2 text-primary" />
            <p className="text-2xl font-bold">{stats.total}</p>
            <p className="text-xs text-muted-foreground">Propiedades</p>
          </div>
          <div className="bg-background border border-border rounded-lg p-4 text-center">
            <Eye className="w-5 h-5 mx-auto mb-2 text-blue-500" />
            <p className="text-2xl font-bold">{stats.totalViews}</p>
            <p className="text-xs text-muted-foreground">Vistas Totales</p>
          </div>
          <div className="bg-background border border-border rounded-lg p-4 text-center">
            <Heart className="w-5 h-5 mx-auto mb-2 text-red-500" />
            <p className="text-2xl font-bold">{stats.totalFavorites}</p>
            <p className="text-xs text-muted-foreground">Favoritos</p>
          </div>
          <div className="bg-background border border-border rounded-lg p-4 text-center">
            <MessageSquare className="w-5 h-5 mx-auto mb-2 text-green-500" />
            <p className="text-2xl font-bold">{stats.totalContacts}</p>
            <p className="text-xs text-muted-foreground">Contactos</p>
          </div>
        </div>
      )}

      {/* Developer profile */}
      {developerProfile &&
        (role === "admin" || role === "developer") &&
        paymentStatus === "active" && (
          <div className="bg-gradient-to-r from-purple-900 to-indigo-900 text-white rounded-lg p-4 sm:p-6 mb-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h2
                  className="text-lg sm:text-xl font-bold"
                  style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
                >
                  {developerProfile.company_name}
                </h2>
                <p className="text-sm opacity-80">
                  {developerProfile.specializations?.join(", ")}
                </p>
              </div>
              <div className="flex items-center gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold">
                    {developerProfile.active_developments || 0}
                  </p>
                  <p className="text-xs opacity-80">Desarrollos</p>
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {developerProfile.total_units_sold || 0}
                  </p>
                  <p className="text-xs opacity-80">Unidades Vendidas</p>
                </div>
              </div>
            </div>
          </div>
        )}

      {/* Broker action buttons */}
      {role === "broker" && paymentStatus === "active" && (
        <div className="flex flex-wrap gap-3 mb-6">
          <Button
            onClick={() => navigate("/inventario-brokers")}
            className="flex-1 sm:flex-none"
            data-testid="open-marketplace-btn"
          >
            {`Ver ${NAV_LABELS.collaborationInventory}`}
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate("/broker-profile/edit")}
            className="flex-1 sm:flex-none"
          >
            Mi Perfil de Broker
          </Button>
        </div>
      )}
    </>
  );
};

export { DashboardHeader };
